import { clamp, pointFrom, pointRotateRads, round } from "@excalidraw/math";

import {
  getFlipAdjustedCropPosition,
  getUncroppedWidthAndHeight,
} from "@excalidraw/element";
import { isImageElement, isFrameLikeElement, reapplyAutoLayoutToAllFrames } from "@excalidraw/element";

import type { ElementsMap, ExcalidrawElement } from "@excalidraw/element/types";

import type { Scene } from "@excalidraw/element";

import { t } from "../../i18n";

import StatsDragInput from "./DragInput";
import { moveElement } from "./utils";

import type { DragInputCallbackType, DragFinishedCallbackType } from "./DragInput";
import type { AppState } from "../../types";

interface PositionProps {
  property: "x" | "y";
  element: ExcalidrawElement;
  elementsMap: ElementsMap;
  scene: Scene;
  appState: AppState;
}

const handlePositionChange: DragInputCallbackType<"x" | "y"> = ({
  accumulatedChange,
  instantChange,
  originalElements,
  originalElementsMap,
  shouldChangeByStepSize,
  nextValue,
  property,
  scene,
  originalAppState,
}) => {
  const elementsMap = scene.getNonDeletedElementsMap();
  const origElement = originalElements[0];
  const [cx, cy] = [
    origElement.x + origElement.width / 2,
    origElement.y + origElement.height / 2,
  ];
  const [topLeftX, topLeftY] = pointRotateRads(
    pointFrom(origElement.x, origElement.y),
    pointFrom(cx, cy),
    origElement.angle,
  );

  if (originalAppState.croppingElementId === origElement.id) {
    const element = elementsMap.get(origElement.id);

    if (!element || !isImageElement(element) || !element.crop) {
      return;
    }

    const crop = element.crop;
    let nextCrop = crop;
    const isFlippedByX = element.scale[0] === -1;
    const isFlippedByY = element.scale[1] === -1;
    const { width: uncroppedWidth, height: uncroppedHeight } =
      getUncroppedWidthAndHeight(element);

    if (nextValue !== undefined) {
      if (property === "x") {
        const nextValueInNatural =
          nextValue * (crop.naturalWidth / uncroppedWidth);

        if (isFlippedByX) {
          nextCrop = {
            ...crop,
            x: clamp(
              crop.naturalWidth - nextValueInNatural - crop.width,
              0,
              crop.naturalWidth - crop.width,
            ),
          };
        } else {
          nextCrop = {
            ...crop,
            x: clamp(
              nextValue * (crop.naturalWidth / uncroppedWidth),
              0,
              crop.naturalWidth - crop.width,
            ),
          };
        }
      }

      if (property === "y") {
        nextCrop = {
          ...crop,
          y: clamp(
            nextValue * (crop.naturalHeight / uncroppedHeight),
            0,
            crop.naturalHeight - crop.height,
          ),
        };
      }

      scene.mutateElement(element, {
        crop: nextCrop,
      });

      return;
    }

    const changeInX =
      (property === "x" ? instantChange : 0) * (isFlippedByX ? -1 : 1);
    const changeInY =
      (property === "y" ? instantChange : 0) * (isFlippedByY ? -1 : 1);

    nextCrop = {
      ...crop,
      x: clamp(crop.x + changeInX, 0, crop.naturalWidth - crop.width),
      y: clamp(crop.y + changeInY, 0, crop.naturalHeight - crop.height),
    };

    scene.mutateElement(element, {
      crop: nextCrop,
    });

    return;
  }

  if (nextValue !== undefined) {
    let newTopLeftX = property === "x" ? nextValue : topLeftX;
    let newTopLeftY = property === "y" ? nextValue : topLeftY;

    // Convert frame-relative coordinates back to absolute coordinates
    if (origElement.frameId) {
      const containingFrame = originalElementsMap.get(origElement.frameId);
      if (containingFrame) {
        if (property === "x") {
          newTopLeftX = nextValue + containingFrame.x;
        } else {
          newTopLeftY = nextValue + containingFrame.y;
        }
      }
    }

    moveElement(
      newTopLeftX,
      newTopLeftY,
      origElement,
      scene,
      originalElementsMap,
    );

    // Check if element is child of auto layout frame and trigger rerendering
    if (origElement.frameId) {
      const parentFrame = originalElementsMap.get(origElement.frameId);
      if (parentFrame && isFrameLikeElement(parentFrame) && 
          (parentFrame as any).autoLayout?.enabled) {
        // Trigger auto layout rerendering for all frames
        const allElements = scene.getElementsIncludingDeleted();
        const elementsMap = scene.getNonDeletedElementsMap();
        const finalElements = reapplyAutoLayoutToAllFrames(allElements, elementsMap);
        scene.replaceAllElements(finalElements);
      }
    }
    return;
  }

  // Snap to 4px grid when dragging
  const snapTo4px = (value: number) => Math.round(value / 4) * 4;

  const changeInTopX = property === "x" ? snapTo4px(accumulatedChange) : 0;
  const changeInTopY = property === "y" ? snapTo4px(accumulatedChange) : 0;

  const newTopLeftX = topLeftX + changeInTopX;
  const newTopLeftY = topLeftY + changeInTopY;

  moveElement(
    newTopLeftX,
    newTopLeftY,
    origElement,
    scene,
    originalElementsMap,
  );

  // Check if element is child of auto layout frame and trigger rerendering
  if (origElement.frameId) {
    const parentFrame = originalElementsMap.get(origElement.frameId);
    if (parentFrame && isFrameLikeElement(parentFrame) && 
        (parentFrame as any).autoLayout?.enabled) {
      // Trigger auto layout rerendering for all frames
      const allElements = scene.getElementsIncludingDeleted();
      const elementsMap = scene.getNonDeletedElementsMap();
      const finalElements = reapplyAutoLayoutToAllFrames(allElements, elementsMap);
      scene.replaceAllElements(finalElements);
    }
  }
};

const handlePositionDragFinished: DragFinishedCallbackType = ({
  app,
  originalElements,
}) => {
  // Check if any moved elements are children of auto layout frames
  const elementsMap = app.scene.getNonDeletedElementsMap();
  const needsAutoLayoutUpdate = originalElements?.some((element) => {
    if (element.frameId) {
      const parentFrame = elementsMap.get(element.frameId);
      return parentFrame && isFrameLikeElement(parentFrame) && 
             (parentFrame as any).autoLayout?.enabled;
    }
    return false;
  });

  if (needsAutoLayoutUpdate) {
    // Trigger auto layout rerendering for all frames
    const allElements = app.scene.getElementsIncludingDeleted();
    const newElementsMap = app.scene.getNonDeletedElementsMap();
    const finalElements = reapplyAutoLayoutToAllFrames(allElements, newElementsMap);
    app.scene.replaceAllElements(finalElements);
  }
};

/**
 * Calculates frame-relative coordinates if element is inside a frame,
 * otherwise returns the absolute coordinates
 */
const getDisplayCoordinates = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
  topLeftX: number,
  topLeftY: number,
) => {
  // Check if element is inside a frame
  if (element.frameId) {
    const containingFrame = elementsMap.get(element.frameId);
    if (containingFrame) {
      // Return frame-relative coordinates (frame's top-left as 0,0)
      return {
        x: topLeftX - containingFrame.x,
        y: topLeftY - containingFrame.y,
      };
    }
  }

  // Return absolute coordinates if not in frame
  return {
    x: topLeftX,
    y: topLeftY,
  };
};

const Position = ({
  property,
  element,
  elementsMap,
  scene,
  appState,
}: PositionProps) => {
  const [topLeftX, topLeftY] = pointRotateRads(
    pointFrom(element.x, element.y),
    pointFrom(element.x + element.width / 2, element.y + element.height / 2),
    element.angle,
  );

  // Get frame-relative or absolute coordinates based on element's frame status
  const displayCoords = getDisplayCoordinates(
    element,
    elementsMap,
    topLeftX,
    topLeftY,
  );
  let value = round(property === "x" ? displayCoords.x : displayCoords.y, 2);

  if (
    appState.croppingElementId === element.id &&
    isImageElement(element) &&
    element.crop
  ) {
    const flipAdjustedPosition = getFlipAdjustedCropPosition(element);

    if (flipAdjustedPosition) {
      value = round(
        property === "x" ? flipAdjustedPosition.x : flipAdjustedPosition.y,
        2,
      );
    }
  }

  return (
    <StatsDragInput
      label={property === "x" ? "X" : "Y"}
      elements={[element]}
      dragInputCallback={handlePositionChange}
      scene={scene}
      value={value}
      property={property}
      appState={appState}
      dragFinishedCallback={handlePositionDragFinished}
      tooltip={t(`stats.tooltips.${property}`)}
    />
  );
};

export default Position;
